import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { EZ5050TradingHeader } from '../components/trading/EZ5050TradingHeader';
import { EZ5050Sidebar } from '../components/trading/EZ5050Sidebar';
import { LogoutModal } from '../components/LogoutModal';
import { TradeWheelModal } from '../components/TradeWheelModal';
import { ResultModal } from '../components/ResultModal';
import { playMultiplierSound } from '../components/Soundmaker';
import { clearAuthSession } from '../lib/authSession';
import { useWallet } from '../contexts/walletContext';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { useLocation } from 'react-router-dom';
import Axios from '../Axios';
import consts from '../constant';

export function EZ5050AppLayout({ children, hideOutlet = false }) {
  const navigate = useNavigate();
  const { wallet, setWallet } = useWallet();
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Lucky Wheel Modal state & settings
  const [isWheelModalOpen, setIsWheelModalOpen] = useState(false);
  const [wheelVariant, setWheelVariant] = useState('spin');
  const [spinAndWinSettings, setSpinAndWinSettings] = useState(null);
  const [jackpotSettings, setJackpotSettings] = useState(null);
  const [showMultiplierModal, setShowMultiplierModal] = useState(false);
  const [multiplierValue, setMultiplierValue] = useState(0);

  const balance = wallet?.balance ?? 0;
  const credit = wallet?.credit ?? 0;

  const location = useLocation();

  const getWallet = useCallback(async () => {
    try {
      const { data } = await Axios.post("/users/getWallet", {});
      if (data?.success) {
        const hasSpinData = data.result?.spin_and_win?.hasSpin;
        setWallet(prev => ({ ...prev, ...data.result, hasSpin: hasSpinData }));
      }
    } catch (error) {
      console.error("Error fetching wallet in layout:", error);
    }
  }, [setWallet]);

  const getSpinAndWinSettings = async () => {
    try {
      const { data } = await Axios.post("/get-trade-settings", {
        type: "spinAndWin",
      });
      if (data?.success) {
        setSpinAndWinSettings(data.result?.spinAndWin || {});
      }
    } catch (error) {
      console.error("Error fetching spin and win settings:", error);
    }
  };

  const getJackpotSettings = async () => {
    try {
      const { data } = await Axios.post("/get-trade-settings", {
        type: "jackpot",
      });
      if (data?.success) {
        setJackpotSettings(data.result?.jackpot || {});
      }
    } catch (error) {
      console.error("Error fetching jackpot settings:", error);
    }
  };

  useEffect(() => {
    getWallet();
    getSpinAndWinSettings();
    getJackpotSettings();
  }, [getWallet]);

  // Global listener to trigger lucky wheel from any child component or header
  useEffect(() => {
    const handleOpenWheel = (e) => {
      const variant = e?.detail?.variant || 'spin';
      setWheelVariant(variant);
      setIsWheelModalOpen(true);
    };
    window.addEventListener('open-lucky-wheel', handleOpenWheel);
    return () => window.removeEventListener('open-lucky-wheel', handleOpenWheel);
  }, []);



  // Trigger Lucky Wheel ONCE at the beginning of play if user has available spins
  const hasAutoOpenedAtStartRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (hasAutoOpenedAtStartRef.current) return;
    const sessionKey = 'lucky_wheel_start_opened';
    try {
      if (sessionStorage.getItem(sessionKey)) {
        hasAutoOpenedAtStartRef.current = true;
        return;
      }
    } catch { }

    const hasSpinData = wallet?.hasSpin || wallet?.spin_and_win?.hasSpin;
    const spins = Number(hasSpinData?.spinCount || 0);

    if (hasSpinData?.success && spins > 0) {
      hasAutoOpenedAtStartRef.current = true;
      try {
        sessionStorage.setItem(sessionKey, 'true');
      } catch { }
      const timer = setTimeout(() => {
        setWheelVariant("spin");
        setIsWheelModalOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [wallet?.hasSpin, wallet?.spin_and_win?.hasSpin]);

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const getActivePage = () => {
    const path = location.pathname;
    if (path.includes('dashboard')) return 'dashboard';
    if (path.includes('futures') || path.includes('trade')) return 'futures';
    if (path.includes('history')) return 'history';
    if (path.includes('leaderboard')) return 'leaderboard';
    if (path.includes('spin-wheel') || path.includes('jackpot')) return 'spin-wheel';
    if (path.includes('settings')) return 'settings';
    return 'dashboard';
  };

  const logout = async () => {
    try {
      const { data } = await Axios.post("/logout");
      if (data.success) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const handleLogout = async () => {
    const logoutStatus = await logout();
    if (logoutStatus) {
      setIsLogoutModalOpen(false);
      clearAuthSession();
      window.location.href = `${consts.CRMUrl}/login`;
    }
  };

  return (
    <div className="flex flex-col w-full overflow-hidden select-none"
      style={{ background: '#0b0e14', color: '#e2e8f0', height: '100dvh', fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      
      {/* Top Header */}
      <EZ5050TradingHeader
        jackpotAmount={14303.63}
        demoBalance={balance}
        realBalance={balance}
        credit={credit}
        accountType="DEMO"
        soundEnabled={soundEnabled}
        onToggleSound={() => setSoundEnabled(!soundEnabled)}
        onLogoutClick={handleLogoutClick}
        onOpenJackpot={() => {
          setWheelVariant("spin");
          setIsWheelModalOpen(true);
        }}
        hideMobile={getActivePage() === 'spin-wheel'}
      />

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden" style={{ minHeight: 0 }}>
        {/* Left Sidebar (Desktop Only) */}
        <div className="hidden md:flex flex-col h-full border-r" style={{ borderColor: '#1e2638' }}>
          <EZ5050Sidebar onLogoutClick={handleLogoutClick} />
        </div>

        {/* Page Content */}
        <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden relative pb-[65px] md:pb-0" style={{ minWidth: 0, minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
          {!hideOutlet && <Outlet />}
          {children}
        </div>
      </div>

      {/* Mobile Bottom Navigation (Mobile Only) */}
      <div className="md:hidden">
        <MobileBottomNav activePage={getActivePage()} />
      </div>

      <LogoutModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
      />

      <TradeWheelModal
        open={isWheelModalOpen}
        variant={wheelVariant}
        segments={
          wheelVariant === "jackpot"
            ? jackpotSettings?.spin_settings
            : spinAndWinSettings?.spin_settings
        }
        onClose={() => setIsWheelModalOpen(false)}
        jackpotTickets={wallet?.spin_and_win?.jackpotTicketCount || 0}
        poolAmount={wallet?.spin_and_win?.pool_amount || "0"}
        onSpinSuccess={() => {
          getWallet();
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("refresh-lucky-details"));
          }
        }}
        hasSpin={wallet?.hasSpin || wallet?.spin_and_win?.hasSpin}
        onMultiplierReward={(value) => {
          const match = String(value || "").match(/(\d+(?:\.\d+)?)/);
          const cleanMult = match ? match[1] : "2";
          setMultiplierValue(cleanMult);
          setShowMultiplierModal(true);
          playMultiplierSound();
        }}
      />

      {showMultiplierModal && (
        <ResultModal
          open={showMultiplierModal}
          variant="multiplier"
          title="Multiplier Achieved!"
          multiplier={`${multiplierValue}X`}
          subtitle="This multiplier will take effect on your next prediction."
          subtitle2="Increase your prediction amount next round to maximize your potential reward."
          onClose={() => {
            setShowMultiplierModal(false);
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("refresh-lucky-details"));
            }
          }}
        />
      )}
    </div>
  );
}
